import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { writeFetch, getSession } from '../../api';
import { secureUpload, formatFileSize, requestPresignedDownloadUrl } from '../../services/s3UploadService';

const {
  FiMusic, FiUpload, FiTrash2, FiEdit3, FiCheck, FiX,
  FiPlay, FiPause, FiAlertTriangle, FiRefreshCw, FiPlus,
  FiWind, FiHeadphones, FiSearch, FiEye, FiEyeOff,
  FiChevronDown, FiSave, FiLink
} = FiIcons;

const TABLE = 'soundscape_tracks_1776430731351';

const CATEGORIES = [
  { id: 'hymns',         label: 'Hymns',         icon: FiMusic      },
  { id: 'instrumentals', label: 'Instrumentals', icon: FiHeadphones },
  { id: 'ambience',      label: 'Ambience',      icon: FiWind       },
];

const ACCEPTED_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/aac', 'audio/flac', 'audio/mp4'];

const catColor = (cat) => ({
  hymns:         'text-rose-500 bg-rose-50 border-rose-200',
  instrumentals: 'text-sky-500 bg-sky-50 border-sky-200',
  ambience:      'text-emerald-500 bg-emerald-50 border-emerald-200',
}[cat] || 'text-gray-500 bg-gray-50 border-gray-200');

/* ── helpers ── */
function isPresignedUrl(url = '') {
  return url.includes('X-Amz-Signature') || url.includes('x-amz-signature');
}

/**
 * Resolves the best playable URL for an S3 track.
 * Pre-signed URLs are refreshed via the server; public URLs used directly.
 */
async function resolveTrackUrl(track, ownerId) {
  const stored = track.s3_url || '';
  if (!stored) throw new Error('No audio URL for this track.');

  if (isPresignedUrl(stored) && track.s3_key) {
    return await requestPresignedDownloadUrl({ s3Key: track.s3_key, ownerId: ownerId || 'admin' });
  }
  return stored;
}

/* ── Upload Drop Zone ── */
function DropZone({ onFile, uploading }) {
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);

  const handle = (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|aac|flac|m4a)$/i)) {
      alert('Only audio files (MP3, WAV, OGG, AAC, FLAC) are accepted.');
      return;
    }
    onFile(file);
  };

  return (
    <div
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); handle(e.dataTransfer.files[0]); }}
      onClick={() => !uploading && inputRef.current?.click()}
      className={`relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
        over ? 'border-accent-gold bg-accent-gold/5 scale-[1.01]' : 'border-brand-dark hover:border-accent-gold/60 bg-brand-light'
      } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
    >
      <input ref={inputRef} type="file" accept="audio/*" className="hidden"
        onChange={e => handle(e.target.files[0])} />
      <div className="w-14 h-14 bg-white rounded-2xl border border-brand-dark flex items-center justify-center mx-auto mb-4 shadow-sm">
        <SafeIcon icon={FiUpload} className="w-6 h-6 text-accent-gold" />
      </div>
      <p className="text-sm font-bold text-accent-navy mb-1">
        {uploading ? 'Uploading…' : 'Drop audio file here or click to browse'}
      </p>
      <p className="text-[10px] text-text-muted">MP3, WAV, OGG, AAC, FLAC — max 100 MB</p>
    </div>
  );
}

/* ── Upload Progress Bar ── */
function UploadBar({ pct }) {
  return (
    <div className="w-full h-2 bg-brand-dark rounded-full overflow-hidden">
      <motion.div className="h-full bg-accent-gold rounded-full"
        animate={{ width: `${pct}%` }} transition={{ ease: 'linear' }} />
    </div>
  );
}

/* ── Inline Edit Modal ── */
function EditModal({ track, onClose, onSave }) {
  const [form, setForm] = useState({
    label:       track.label || '',
    description: track.description || '',
    category:    track.category || 'ambience',
    is_active:   track.is_active === 1 || track.is_active === '1',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(track.id, { ...form, is_active: form.is_active ? 1 : 0 });
    setSaving(false);
    onClose();
  };

  return (
    <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <motion.div className="bg-white rounded-[32px] w-full max-w-md shadow-2xl"
        initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9 }}>
        <div className="p-7">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-serif text-accent-navy">Edit Track</h3>
            <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-xl transition-colors">
              <SafeIcon icon={FiX} className="w-4 h-4 text-text-muted" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-text-muted block mb-1.5">Track Label *</label>
              <input value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} required
                className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-text-muted block mb-1.5">Description</label>
              <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-text-muted block mb-1.5">Category</label>
              <div className="relative">
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light appearance-none">
                  {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer p-3 bg-brand-light rounded-2xl border border-brand-dark">
              <div onClick={() => setForm(p => ({ ...p, is_active: !p.is_active }))}
                className={`w-11 h-6 rounded-full relative transition-colors flex items-center px-1 ${form.is_active ? 'bg-accent-gold' : 'bg-gray-300'}`}>
                <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_active ? 'translate-x-5' : 'translate-x-0'}`} />
              </div>
              <span className="text-sm font-bold text-accent-navy">Active (visible to visitors)</span>
            </label>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose}
                className="flex-1 py-3 border-2 border-brand-dark rounded-2xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-navy transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="flex-1 py-3 bg-accent-gold text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {saving
                  ? <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />
                  : <SafeIcon icon={FiSave} className="w-4 h-4" />
                }
                Save
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Track Row ── */
function TrackRow({ track, onEdit, onDelete, onToggle, ownerId }) {
  const [playing,     setPlaying]     = useState(false);
  const [resolving,   setResolving]   = useState(false);
  const [previewErr,  setPreviewErr]  = useState('');
  const audioRef = useRef(null);
  const isActive = track.is_active === 1 || track.is_active === '1';

  const togglePlay = async () => {
    const el = audioRef.current;
    if (!el) return;

    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }

    setResolving(true);
    setPreviewErr('');
    try {
      const url = await resolveTrackUrl(track, ownerId);
      el.src = url;
      await el.play();
      setPlaying(true);
    } catch (err) {
      setPreviewErr('Preview unavailable');
      console.warn('[AudioFileManager] Preview failed:', err.message);
    } finally {
      setResolving(false);
    }
  };

  useEffect(() => () => audioRef.current?.pause(), []);

  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className={`flex items-center gap-4 p-4 rounded-2xl border transition-all group ${
        isActive
          ? 'bg-white border-brand-dark hover:border-accent-gold/40'
          : 'bg-gray-50/50 border-dashed border-brand-dark/40 opacity-60'
      }`}>
      <audio ref={audioRef} onEnded={() => setPlaying(false)} />

      {/* Play preview */}
      <button onClick={togglePlay} disabled={resolving}
        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border-2 transition-all ${
          playing
            ? 'bg-accent-gold border-accent-gold text-white'
            : resolving
            ? 'bg-brand-light border-brand-dark text-text-muted cursor-wait'
            : 'bg-brand-light border-brand-dark hover:border-accent-gold text-accent-navy'
        }`}>
        {resolving
          ? <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
          : <SafeIcon icon={playing ? FiPause : FiPlay} className="w-4 h-4" />
        }
      </button>

      {/* Meta */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-accent-navy truncate flex items-center gap-2">
          {track.label}
          {!track.s3_key && track.s3_url && (
            <span title="External URL Track">
              <SafeIcon icon={FiLink} className="w-3 h-3 text-text-muted" />
            </span>
          )}
        </p>
        <p className="text-[10px] text-text-muted truncate">{track.description || '—'}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className={`text-[9px] px-2 py-0.5 rounded-full font-black uppercase tracking-widest border ${catColor(track.category)}`}>
            {track.category}
          </span>
          {track.file_size > 0 && (
            <span className="text-[9px] text-text-muted font-mono">{formatFileSize(track.file_size)}</span>
          )}
          <span className="text-[9px] text-text-muted font-mono truncate max-w-[120px]">{track.file_name}</span>
          {previewErr && (
            <span className="text-[9px] text-red-500 font-bold">{previewErr}</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => onToggle(track)} title={isActive ? 'Deactivate' : 'Activate'}
          className={`p-2 rounded-xl border transition-all ${
            isActive
              ? 'border-amber-200 text-amber-600 hover:bg-amber-50'
              : 'border-green-200 text-green-600 hover:bg-green-50'
          }`}>
          <SafeIcon icon={isActive ? FiEyeOff : FiEye} className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onEdit(track)}
          className="p-2 rounded-xl border border-brand-dark text-text-muted hover:border-accent-gold hover:text-accent-gold transition-all">
          <SafeIcon icon={FiEdit3} className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onDelete(track)}
          className="p-2 rounded-xl border border-red-200 text-red-400 hover:bg-red-50 transition-all">
          <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
}

/* ── Main Component ── */
export default function AudioFileManager() {
  const user = getSession();
  const ownerId = user?.id || 'admin';

  const [tracks,    setTracks]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [search,    setSearch]    = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [editTrack, setEditTrack] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const [uploadMode,  setUploadMode]  = useState('file'); // 'file' or 'url'
  const [urlInput,    setUrlInput]    = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadMeta,  setUploadMeta]  = useState({ label: '', description: '', category: 'hymns' });
  const [uploading,   setUploading]   = useState(false);
  const [uploadPct,   setUploadPct]   = useState(0);
  const [uploadErr,   setUploadErr]   = useState('');
  const [uploadDone,  setUploadDone]  = useState(false);

  /* ── fetch ── */
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res  = await writeFetch(`read/${TABLE}?sort=created_at&order=desc&limit=200`);
      const data = await res.json();
      setTracks(Array.isArray(data.data) ? data.data : []);
    } catch { setError('Failed to load tracks.'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── upload ── */
  const handleUpload = async () => {
    if (uploadMode === 'file' && !pendingFile) {
        setUploadErr('Please select a file to upload.');
        return;
    }
    if (uploadMode === 'url' && !urlInput.trim()) {
        setUploadErr('Please provide a valid audio URL.');
        return;
    }
    if (!uploadMeta.label.trim()) {
      setUploadErr('Please provide a track label.');
      return;
    }

    setUploading(true); setUploadPct(0); setUploadErr('');
    try {
      let s3Key = null;
      let s3Url = urlInput.trim();
      let fileName = 'External URL';
      let fileSize = 0;
      let fileType = 'audio/mpeg';

      if (uploadMode === 'file') {
          const { s3Key: upKey, s3Url: upUrl } = await secureUpload({
            file:       pendingFile,
            ownerId,
            category:   'soundscape',
            onProgress: setUploadPct,
          });
          s3Key = upKey;
          s3Url = upUrl;
          fileName = pendingFile.name;
          fileSize = pendingFile.size;
          fileType = pendingFile.type || 'audio/mpeg';
      }

      await writeFetch(`create/${TABLE}`, {
        method: 'POST',
        body: JSON.stringify({
          id:          crypto.randomUUID(),
          owner_id:    ownerId,
          label:       uploadMeta.label.trim(),
          description: uploadMeta.description.trim(),
          category:    uploadMeta.category,
          s3_key:      s3Key,
          s3_url:      s3Url,
          file_name:   fileName,
          file_size:   fileSize,
          file_type:   fileType,
          is_active:   1,
          sort_order:  tracks.length,
        }),
      });

      setUploadDone(true);
      setPendingFile(null);
      setUrlInput('');
      setUploadMeta({ label: '', description: '', category: 'hymns' });
      setTimeout(() => setUploadDone(false), 3000);
      await load();
    } catch (e) {
      setUploadErr(e.message || 'Upload failed.');
    }
    setUploading(false);
  };

  const handleSave = async (id, updates) => {
    await writeFetch(`update/${TABLE}/${id}`, { method: 'PUT', body: JSON.stringify(updates) });
    await load();
  };

  const handleToggle = async (track) => {
    const newVal = (track.is_active === 1 || track.is_active === '1') ? 0 : 1;
    await writeFetch(`update/${TABLE}/${track.id}`, { method: 'PUT', body: JSON.stringify({ is_active: newVal }) });
    await load();
  };

  const handleDelete = async (track) => {
    await writeFetch(`delete/${TABLE}/${track.id}`, { method: 'DELETE' });
    setDelTarget(null);
    await load();
  };

  const filtered = tracks.filter(t => {
    const q = search.toLowerCase();
    const matchSearch = !q || t.label?.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q);
    const matchCat    = catFilter === 'all' || t.category === catFilter;
    return matchSearch && matchCat;
  });

  const stats = {
    total:  tracks.length,
    active: tracks.filter(t => t.is_active === 1 || t.is_active === '1').length,
    hymns:  tracks.filter(t => t.category === 'hymns').length,
    inst:   tracks.filter(t => t.category === 'instrumentals').length,
    amb:    tracks.filter(t => t.category === 'ambience').length,
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 via-accent-navy to-indigo-900 text-white rounded-[32px] p-8 flex items-center gap-6 relative overflow-hidden shadow-2xl">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #fff 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="w-16 h-16 bg-accent-gold text-accent-navy rounded-2xl flex items-center justify-center shadow-xl flex-shrink-0 z-10">
          <SafeIcon icon={FiMusic} className="w-8 h-8" />
        </div>
        <div className="z-10 flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h2 className="font-serif text-2xl">Soundscape Audio Manager</h2>
            <span className="text-[9px] px-3 py-1 bg-accent-gold text-accent-navy rounded-full font-black uppercase tracking-widest">Super Admin</span>
          </div>
          <p className="text-white/60 text-sm font-light">
            Upload and manage memorial soundscape tracks. Files are stored securely in S3 and served to all memorial pages.
          </p>
        </div>
        <div className="z-10 hidden md:grid grid-cols-2 gap-3 flex-shrink-0">
          {[
            { label: 'Total Tracks', value: stats.total  },
            { label: 'Active',       value: stats.active },
          ].map(s => (
            <div key={s.label} className="bg-white/10 rounded-2xl p-4 text-center border border-white/10">
              <p className="text-2xl font-serif font-bold text-accent-gold">{s.value}</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-white/50 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Hymns',         count: stats.hymns, icon: FiMusic,      color: 'bg-rose-50 border-rose-200 text-rose-500' },
          { label: 'Instrumentals', count: stats.inst,  icon: FiHeadphones, color: 'bg-sky-50 border-sky-200 text-sky-500' },
          { label: 'Ambience',      count: stats.amb,   icon: FiWind,       color: 'bg-emerald-50 border-emerald-200 text-emerald-500' },
        ].map(c => (
          <div key={c.label} className={`p-4 rounded-2xl border flex items-center justify-between ${c.color}`}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-xl shadow-sm"><SafeIcon icon={c.icon} className="w-4 h-4" /></div>
              <p className="font-bold text-sm text-accent-navy">{c.label}</p>
            </div>
            <p className="text-xl font-serif font-bold opacity-80">{c.count}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Upload Panel */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-[32px] border border-brand-dark p-6 shadow-sm">
            <h3 className="font-serif text-lg text-accent-navy mb-4">Add New Track</h3>
            
            <div className="flex bg-brand-light rounded-xl p-1 mb-5 border border-brand-dark">
                <button
                    onClick={() => setUploadMode('file')}
                    className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${uploadMode === 'file' ? 'bg-white shadow-sm text-accent-navy' : 'text-text-muted hover:text-accent-navy'}`}
                >
                    File Upload
                </button>
                <button
                    onClick={() => setUploadMode('url')}
                    className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${uploadMode === 'url' ? 'bg-white shadow-sm text-accent-navy' : 'text-text-muted hover:text-accent-navy'}`}
                >
                    URL Input
                </button>
            </div>

            {uploadMode === 'file' ? (
                !pendingFile ? (
                <DropZone onFile={setPendingFile} uploading={uploading} />
                ) : (
                <div className="p-4 border border-brand-dark rounded-2xl bg-brand-light mb-6 flex items-center justify-between">
                    <div className="min-w-0 pr-4">
                    <p className="text-sm font-bold text-accent-navy truncate">{pendingFile.name}</p>
                    <p className="text-[10px] text-text-muted font-mono">{formatFileSize(pendingFile.size)}</p>
                    </div>
                    {!uploading && (
                    <button onClick={() => setPendingFile(null)} className="p-2 text-text-muted hover:text-red-500 transition-colors">
                        <SafeIcon icon={FiX} className="w-4 h-4" />
                    </button>
                    )}
                </div>
                )
            ) : (
                <div className="mb-6 space-y-3">
                   <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-text-muted block mb-1.5">Audio URL</label>
                        <input
                            type="url"
                            placeholder="https://example.com/audio.mp3"
                            value={urlInput}
                            onChange={e => setUrlInput(e.target.value)}
                            disabled={uploading}
                            className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light"
                        />
                   </div>
                </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-text-muted block mb-1.5">Track Label *</label>
                <input
                  type="text"
                  placeholder="e.g. Amazing Grace"
                  value={uploadMeta.label}
                  onChange={e => setUploadMeta(p => ({ ...p, label: e.target.value }))}
                  disabled={uploading}
                  className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light"
                />
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-text-muted block mb-1.5">Category</label>
                <div className="relative">
                  <select
                    value={uploadMeta.category}
                    onChange={e => setUploadMeta(p => ({ ...p, category: e.target.value }))}
                    disabled={uploading}
                    className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light appearance-none"
                  >
                    {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                  <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-text-muted block mb-1.5">Description (Optional)</label>
                <input
                  type="text"
                  placeholder="Short description..."
                  value={uploadMeta.description}
                  onChange={e => setUploadMeta(p => ({ ...p, description: e.target.value }))}
                  disabled={uploading}
                  className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light"
                />
              </div>
            </div>

            {uploadErr && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2 text-red-600">
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 shrink-0 mt-0.5" />
                <p className="text-xs">{uploadErr}</p>
              </div>
            )}

            {uploading && (
              <div className="mt-6 space-y-2">
                <div className="flex justify-between text-xs font-bold text-accent-navy">
                  <span>Uploading to S3...</span>
                  <span>{uploadPct}%</span>
                </div>
                <UploadBar pct={uploadPct} />
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={uploading || (uploadMode === 'file' ? !pendingFile : !urlInput.trim()) || !uploadMeta.label.trim()}
              className="w-full mt-6 py-4 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </>
              ) : uploadDone ? (
                <>
                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                  Uploaded Successfully!
                </>
              ) : (
                <>
                  <SafeIcon icon={uploadMode === 'file' ? FiUpload : FiLink} className="w-4 h-4" />
                  {uploadMode === 'file' ? 'Upload Track' : 'Save Track Link'}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Tracks List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-[32px] border border-brand-dark p-6 shadow-sm min-h-[500px] flex flex-col">
            
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
              <div className="relative flex-1 w-full">
                <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search tracks..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light"
                />
              </div>
              <div className="relative w-full sm:w-48">
                <select
                  value={catFilter}
                  onChange={e => setCatFilter(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm bg-brand-light appearance-none font-bold"
                >
                  <option value="all">All Categories</option>
                  {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
              </div>
              <button
                onClick={load}
                className="p-3 bg-brand-light border-2 border-brand-dark rounded-2xl text-accent-navy hover:border-accent-gold transition-colors flex-shrink-0"
                title="Refresh List"
              >
                <SafeIcon icon={FiRefreshCw} className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto pr-2 space-y-3 pb-4 styled-scrollbar">
              {loading && tracks.length === 0 ? (
                <div className="h-full flex items-center justify-center text-text-muted">
                  <div className="w-8 h-8 border-4 border-accent-gold/30 border-t-accent-gold rounded-full animate-spin" />
                </div>
              ) : error ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center bg-red-50 p-6 rounded-2xl border border-red-200">
                    <SafeIcon icon={FiAlertTriangle} className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="text-sm font-bold text-red-700">{error}</p>
                    <button onClick={load} className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg text-xs font-bold hover:bg-red-200">
                      Retry
                    </button>
                  </div>
                </div>
              ) : filtered.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-8 opacity-60">
                  <SafeIcon icon={FiMusic} className="w-12 h-12 text-text-muted mb-4" />
                  <p className="text-lg font-serif text-accent-navy">No tracks found</p>
                  <p className="text-sm text-text-muted mt-1">Try adjusting your search or upload a new track.</p>
                </div>
              ) : (
                <AnimatePresence mode="popLayout">
                  {filtered.map(track => (
                    <TrackRow
                      key={track.id}
                      track={track}
                      onEdit={setEditTrack}
                      onDelete={setDelTarget}
                      onToggle={handleToggle}
                      ownerId={ownerId}
                    />
                  ))}
                </AnimatePresence>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {editTrack && (
          <EditModal
            track={editTrack}
            onClose={() => setEditTrack(null)}
            onSave={handleSave}
          />
        )}
        {delTarget && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="bg-white rounded-[32px] w-full max-w-sm shadow-2xl p-8 text-center"
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9 }}>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiTrash2} className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="font-serif text-xl text-accent-navy mb-2">Delete Track?</h3>
              <p className="text-sm text-text-muted mb-6">
                Are you sure you want to delete <strong className="text-accent-navy">{delTarget.label}</strong>? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setDelTarget(null)}
                  className="flex-1 py-3 border-2 border-brand-dark rounded-2xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-navy transition-colors">
                  Cancel
                </button>
                <button onClick={() => handleDelete(delTarget)}
                  className="flex-1 py-3 bg-red-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-red-600 transition-colors">
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}